/**
 * ClienteDetailsPage - Sprint 3
 *
 * Página de detalhes do Cliente com gestão de vínculos de produtos
 * Features: Visualizar cliente, gerenciar produtos vinculados
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, RefreshCw, Edit, Mail, Send, Server } from 'lucide-react';
import { Header } from '../components/Header';
import { MainLayout } from '../components/MainLayout';
import PermissionGate from '../components/PermissionGate';
import clienteService, { Cliente } from '../services/clienteService';
import ClienteProdutosGrid from '../components/ClienteProdutosGrid';
import ClienteFormModal from '../components/ClienteFormModal';
import SMTPTestModal from '../components/SMTPTestModal';
import { mascaraTelefone } from '../lib/utils';

const ClienteDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadCliente(parseInt(id));
    }
  }, [id]);

  const loadCliente = async (clienteId: number) => {
    setLoading(true);
    setError('');
    try {
      const data = await clienteService.getCliente(clienteId);
      setCliente(data);
    } catch (err: any) {
      setError('Erro ao carregar cliente');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCliente = async () => {
    if (id) {
      await loadCliente(parseInt(id));
      setIsEditModalOpen(false);
    }
  };

  if (loading) {
    return (
      <>
        <MainLayout>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Carregando cliente...</p>
            </div>
          </div>
        </MainLayout>
      </>
    );
  }

  if (error || !cliente) {
    return (
      <>
        <MainLayout>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-12 text-center">
              <p className="text-red-800 mb-4">{error || 'Cliente não encontrado'}</p>
              <button
                onClick={() => navigate('/cloud/clientes')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Voltar para Clientes
              </button>
            </div>
          </div>
        </MainLayout>
      </>
    );
  }

  return (
    <>
      <MainLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/cloud/clientes')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Clientes
          </button>

          {/* Cliente Info Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-blue-600" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {cliente.nome_fantasia || cliente.razao_social}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {cliente.codigo}
                  </p>
                </div>
              </div>
              <span
                className={`px-3 py-1 text-sm font-semibold rounded-full ${
                  cliente.ativo
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {cliente.ativo ? 'Ativo' : 'Inativo'}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cliente.cnpj && (
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    CNPJ
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {clienteService.formatCNPJ(cliente.cnpj)}
                  </p>
                </div>
              )}
              {cliente.telefone && (
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Telefone
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {mascaraTelefone(cliente.telefone)}
                  </p>
                </div>
              )}
              {cliente.email && (
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Email
                  </label>
                  <p className="text-gray-900 dark:text-white">{cliente.email}</p>
                </div>
              )}
              {cliente.endereco && (
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Endereço
                  </label>
                  <p className="text-gray-900 dark:text-white">{cliente.endereco}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Período de Segregação
                </label>
                <p className="text-gray-900 dark:text-white">
                  {cliente.periodo_segregacao} dias
                </p>
              </div>
            </div>
          </div>

          {/* SMTP Configuration Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Server className="w-6 h-6 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Configuração SMTP
                </h3>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsTestModalOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  disabled={!cliente.smtp_servidor}
                  title={!cliente.smtp_servidor ? 'Configure o SMTP primeiro' : 'Testar envio de email'}
                >
                  <Send className="w-4 h-4" />
                  Testar SMTP
                </button>
                <PermissionGate tela="CLIENTES_LIST" acao="editar">
                  <button
                    onClick={() => setIsEditModalOpen(true)}
                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                  >
                    <Edit className="w-4 h-4" />
                    Editar
                  </button>
                </PermissionGate>
              </div>
            </div>

            {cliente.smtp_servidor ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Servidor SMTP
                  </label>
                  <p className="text-gray-900 dark:text-white flex items-center gap-2">
                    <Server className="w-4 h-4 text-gray-400" />
                    {cliente.smtp_servidor}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Porta
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {cliente.smtp_porta || 587}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    TLS/SSL
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {cliente.smtp_tls ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        Ativado
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                        Desativado
                      </span>
                    )}
                  </p>
                </div>
                {cliente.email_copia && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Email para Cópia
                    </label>
                    <p className="text-gray-900 dark:text-white flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {cliente.email_copia}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Mail className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Nenhuma configuração SMTP definida
                </p>
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Configurar SMTP
                </button>
              </div>
            )}
          </div>

          {/* Produtos do Cliente */}
          <ClienteProdutosGrid cliente={cliente} />
        </div>
      </MainLayout>

      {/* Modals */}
      {isEditModalOpen && (
        <ClienteFormModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          cliente={cliente}
          onSave={handleSaveCliente}
        />
      )}

      {isTestModalOpen && cliente && (
        <SMTPTestModal
          isOpen={isTestModalOpen}
          onClose={() => setIsTestModalOpen(false)}
          smtpServidor={cliente.smtp_servidor || ''}
          smtpPorta={cliente.smtp_porta || 587}
          smtpTls={cliente.smtp_tls !== false}
          defaultEmail={cliente.email_copia || ''}
        />
      )}
    </>
  );
};

export default ClienteDetailsPage;
